import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { useUpdateCustomer } from "hooks/useCustomers"; // Import the useUpdateCustomer hook
import { Formik, Form, Field, ErrorMessage } from "formik"; // Import Formik components
import * as Yup from "yup"; // Import Yup for validation
import "./edit.css"; // Import custom styles for modern look

const EditCustomerModal = ({ isOpen, toggle, customer }) => {
  const [tags, setTags] = useState<string[]>(customer?.tags || []); // State for tags, initialized with existing tags
  const [tagInput, setTagInput] = useState<string>(""); // State for tag input
  const { mutate: updateCustomer } = useUpdateCustomer(); // Destructure mutate from the hook

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (tagInput.trim() && !tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
        setTagInput("");
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSave = async (values) => {
    values.tags = tags.length > 0 ? tags.join(",") : "";
    if (tagInput.trim()) {
      values.tags = values.tags
        ? `${values.tags}, ${tagInput.trim()}`
        : tagInput.trim();
    }

    const updatedCustomer = {
      id: customer.id, // Ensure to include the customer ID
      data: {
        id: customer.id, // Add the missing id
        branch_id: customer.branch_id, // Add the missing branch_id
        notes: customer.notes || "", // Add the missing notes, defaulting to an empty string if not present
        created_at: customer.created_at, // Add the missing created_at
        updated_at: new Date().toISOString(), // Add the missing updated_at with the current date
        name: values.name,
        email: values.email,
        credit_balance: values.credit_balance,
        credit_limit: values.credit_limit,
        phone: values.phone,
        tags: values.tags,
      },
    };

    await updateCustomer(updatedCustomer, {
      onSuccess: () => {
        setTagInput("");
        toggle();
      },
    });
  };

  // Validation schema using Yup
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    // email: Yup.string().email("Invalid email").required("Email is required"),
    // phone: Yup.string().required("Phone number is required"),
  });

  const handleCancelClick = () => {
    setTags([]);
    toggle();
  };

  useEffect(() => {
    setTags(customer?.tags ? customer.tags.split(",") : []);
  }, [customer]);

  return (
    <Modal isOpen={isOpen} toggle={handleCancelClick} centered={true}>
      <ModalHeader toggle={handleCancelClick}>Edit Customer</ModalHeader>
      <ModalBody>
        <Formik
          initialValues={{
            name: customer?.name || "",
            email: customer?.email || "",
            phone: customer?.phone || "",
            tags: customer?.tags || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSave}
        >
          {({ handleChange, handleBlur, values }) => (
            <Form>
              <FormGroup>
                <Label for="name">Name</Label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  as={Input}
                />
                <ErrorMessage
                  name="name"
                  component="div"
                  className="text-danger"
                />
              </FormGroup>
              <FormGroup>
                <Label for="email">Email</Label>
                <Field
                  type="email"
                  id="email"
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  as={Input}
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="text-danger"
                />
              </FormGroup>
              <FormGroup>
                <Label for="phone">Phone</Label>
                <Field
                  type="text"
                  id="phone"
                  name="phone"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.phone}
                  as={Input}
                />
                <ErrorMessage
                  name="phone"
                  component="div"
                  className="text-danger"
                />
              </FormGroup>
              <FormGroup>
                <Label for="tags">Tags</Label>
                <div className="tags-input">
                  {Array.isArray(tags) &&
                    tags.map((tag, index) => (
                      <span key={index} className="tag-pill">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)}>
                          x
                        </button>
                      </span>
                    ))}
                  <Input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Add a tag and press Enter"
                  />
                </div>
              </FormGroup>
              <ModalFooter>
                <Button color="primary" type="submit">
                  Save
                </Button>
                <Button color="secondary" onClick={handleCancelClick}>
                  Cancel
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default EditCustomerModal;
